import { useEffect, useState } from "react";

import { TAccount } from "../@types";
import { apis, callApi } from "../api";
import { LearningService } from "../components/paginas/Learning/hooks/useLearning";
import { LearningMessageService } from "../components/paginas/Learning/hooks/useLearningMessage";
import { TeachingService } from "../components/paginas/Teaching/hooks/useTeaching";
import { TeachingMessageService } from "../components/paginas/Teaching/hooks/useTeachingMessage";
import {
	BookingCourseService,
	CheckoutCourseService,
} from "../components/paginas/User/CheckoutCourse/hooks/useCheckoutCourse";
import { PreviewCourseService } from "../components/paginas/User/PreviewCourse/hooks/usePreviewCourse";
import { omitEmpty } from "../utils";
import { ChatService } from "./useChat";
import { InstructorAgendaService } from "./useInstructorAgenda";
import { InstructorSummariesService } from "./useInstructorSummaries";
import { MyLearningProgressService } from "./useMyLearningProgress";
import { MyOwnedCoursesStudentService } from "./useMyOwnedCoursesStudent";
import { MyServicesService } from "./useMyServices";
import { PaymentRequestService } from "./usePaymentRequest";
import { PremiumPackageService } from "./usePremiumPackage";
import { ProfileService } from "./useProfile";
import { ScheduleService } from "./useSchedule";
import { StripeService } from "./useStripe";
import { StudentSummariesService } from "./useStudentSummaries";
import { TeachingPolicyService } from "./useTeachingPolicy";
import { TokenService } from "./useToken";
import { UserDocumentsService } from "./useUserDocuments";

export type TAccountService = {
	savedAccount: TAccount | null;
	listeners: (() => void)[];

	addEventListener: (cb: () => void) => void;
	removeEventListener: (cb: () => void) => void;
	saveAccount: (newAccount: TAccount) => void;
	emitChanges: () => void;
	signOut: () => void;
};

export const AccountService: TAccountService = {
	savedAccount: null,
	listeners: [],

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	saveAccount(newAccount) {
		this.savedAccount = newAccount;
		this.emitChanges();
	},

	signOut() {
		TokenService.clearToken();
		StripeService.clearData();
		InstructorAgendaService.clearData();
		InstructorSummariesService.clearData();
		MyOwnedCoursesStudentService.clearData();
		StudentSummariesService.clearData();
		CheckoutCourseService.clearData();
		BookingCourseService.clearData();
		PreviewCourseService.clearData();
		ProfileService.clearData();
		PaymentRequestService.clearData();
		ChatService.clearData();
		PremiumPackageService.clearData();
		ScheduleService.clearData();
		TeachingPolicyService.clearData();
		MyServicesService.clearData();
		MyLearningProgressService.clearData();
		TeachingService.clearData();
		TeachingMessageService.clearData();
		LearningService.clearData();
		LearningMessageService.clearData();
		UserDocumentsService.clearData();
		this.savedAccount = null;
		this.emitChanges();
	},

	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},
};

export function useAccount() {
	const [account, setAccount] = useState(AccountService.savedAccount);

	useEffect(() => {
		const updateAccount = () => {
			setAccount(AccountService.savedAccount);
		};

		AccountService.addEventListener(updateAccount);

		return () => {
			AccountService.removeEventListener(updateAccount);
		};
	}, []);

	const signOut = () => {
		AccountService.signOut();
	};

	const saveUserInfo = async (data: Partial<TAccount>) => {
		const body = omitEmpty(data);

		try {
			const response = await callApi({
				method: "PATCH",
				url: apis.UPDATE_USER_INFORMATION,
				body,
			});

			if (!response.user) {
				return;
			}

			AccountService.saveAccount(response.user);
		} catch (error) {}
	};

	return { account, signOut, saveUserInfo };
}
